import { PlusOutlined } from "@ant-design/icons";
import { Flex, Form, notification } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    getApiError,
    getSchedulerMyRoutesThunk,
    getUserTeamGetByManagerThunk,
    patchSchedulerSetSchedulePointsThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { Schedule } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import BaseButton from "@/components/BaseButton";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import Meta from "@/components/Meta";
import { BlockLoader } from "@/components/UI/BlockLoader";
import { FloatingActionButton } from "@/components/UI/FloatingActionButton";
import { ROUTES } from "@/constants";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { MarketCardSelectable } from "@/pages/SV/RouteManagement/AddMarket/components/MarketCardSelectable";
import { MarketsSearchInput } from "@/pages/SV/RouteManagement/AddMarket/components/MarketsSearchInput";
import { RegionPicker } from "@/pages/SV/RouteManagement/AddMarket/components/RegionPicker";
import { useMarketsWithoutRoute } from "@/pages/SV/RouteManagement/AddMarket/hooks";
import { ScheduleRepeatForm } from "@/pages/SV/RouteManagement/components/ScheduleRepeatForm";
import { ScheduleRepeatFormFields } from "@/pages/SV/RouteManagement/components/ScheduleRepeatForm/ScheduleRepeatForm.types";
import { getSchedulerAllScheduleSvThunk } from "@/store/slices/sv";
import { setCurrentScheduleList } from "@/store/slices/sv/sv.slice";

import {
    StyledControlsContainer,
    StyledScheduleFormWrapper,
    StyledScrollContainer,
} from "./Styled";

const initialScheduleValues = {
    weekly: 1,
    weekStartDate: dayjs().startOf("week").format(backendDateFormat.date),
    days: [],
};

function AddMarket() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();

    const navigateOptions = { state: location.state, replace: true };

    const [scheduleRepeatForm] = Form.useForm<ScheduleRepeatFormFields>();

    const { routeId } = useParams();

    const { id, regions, role } = useAppSelector((state) => state.user);
    const { team, currentScheduleList, routeList } = useAppSelector(
        (state) => state.sv,
    );

    const [selectedRegionId, setSelectedRegionId] = useState<string>("");

    const [bootLoading, setBootLoading] = useState(false);

    const [checkedMarkets, setCheckedMarkets] = useState<Set<string>>(
        new Set(),
    );

    const routeEditorPath = routeId
        ? isManagerRole(role)
            ? buildRoutePath(ROUTES.RouteManagementEdit, { routeId })
            : ROUTES.RouteManagementMyRoute
        : ROUTES.RouteManagementCreate;

    const {
        markets: marketsWithoutRoute,
        loading: marketsLoading,
        refetch: fetchMarkets,
    } = useMarketsWithoutRoute({
        regionId: selectedRegionId,
        partnerId: team?.partnerId,
        role,
    });

    const displayMarketsWithoutRoute = marketsWithoutRoute.filter(
        ({ id }) =>
            !currentScheduleList.some(({ marketId }) => marketId === id),
    );

    const handleCreateMarket = useCallback(() => {
        navigate(ROUTES.NewRouteMarketCreate);
    }, [navigate]);

    const toggleMarketChecked = (marketId: string) => {
        setCheckedMarkets((prev) => {
            const next = new Set(prev);

            if (next.has(marketId)) next.delete(marketId);
            else next.add(marketId);

            return next;
        });
    };

    useEffect(() => {
        if (team?.mainRegionId && !selectedRegionId) {
            setSelectedRegionId(team.mainRegionId);
        }
    }, [team?.mainRegionId, selectedRegionId]);

    useEffect(() => {
        if (!id) return;

        let alive = true;

        (async () => {
            setBootLoading(true);

            await Promise.allSettled([
                dispatch(getSchedulerMyRoutesThunk({ ownerId: id })).unwrap(),
                dispatch(
                    getUserTeamGetByManagerThunk({ managerId: id }),
                ).unwrap(),
            ]);

            if (alive) setBootLoading(false);
        })();

        return () => {
            alive = false;
        };
    }, [id, dispatch]);

    const handleRegionChange = (value: string) => {
        setSelectedRegionId(value);
    };

    const [handleUpdatePoint, updateLoading] = useAsyncAction(async () => {
        if (!checkedMarkets.size) return;

        let values: ScheduleRepeatFormFields;
        try {
            values = await scheduleRepeatForm.validateFields();
        } catch {
            return;
        }

        const { weekStartDate, weekly, days } = values;

        const newSchedules: Schedule[] = Array.from(checkedMarkets).map(
            (marketId) => ({
                marketId,
                weekly,
                days,
                startDate: weekStartDate,
                sortOrder: 1,
            }),
        );

        const updatedRouteItems = [...currentScheduleList, ...newSchedules];

        if (!routeId) {
            dispatch(setCurrentScheduleList(updatedRouteItems));
            navigate(routeEditorPath, navigateOptions);
            return;
        }

        const currentRoute = routeList?.[routeId];

        try {
            await dispatch(
                patchSchedulerSetSchedulePointsThunk({
                    agentId: currentRoute?.agentId,
                    ownerId: currentRoute?.ownerId,
                    schedules: updatedRouteItems,
                }),
            ).unwrap();

            await dispatch(
                getSchedulerAllScheduleSvThunk({
                    agentId: routeId,
                }),
            );

            navigate(routeEditorPath, navigateOptions);
        } catch (err) {
            console.error(err);
            const { displayMessage } = getApiError(err);

            notification.error({
                message: "Не вдалося додати ТТ на маршрут",
                description: displayMessage,
            });
        }
    });

    const loading = bootLoading || marketsLoading;

    return (
        <>
            <Meta title="Add market" />

            <Header
                title="Додати ТТ"
                path={routeEditorPath}
                locationState={navigateOptions}
            />

            <MainContainer $padding="0">
                <StyledScrollContainer vertical gap={12}>
                    <StyledScheduleFormWrapper>
                        <Form<ScheduleRepeatFormFields>
                            form={scheduleRepeatForm}
                            initialValues={initialScheduleValues}
                        >
                            <ScheduleRepeatForm form={scheduleRepeatForm} />
                        </Form>
                    </StyledScheduleFormWrapper>

                    <RegionPicker
                        regions={regions}
                        value={selectedRegionId}
                        onChange={handleRegionChange}
                    />

                    <MarketsSearchInput
                        loading={loading}
                        fetchMarkets={fetchMarkets}
                    />

                    {loading ? (
                        <Flex flex={1} justify="center">
                            <BlockLoader />
                        </Flex>
                    ) : (
                        <Flex vertical gap={16}>
                            {displayMarketsWithoutRoute.map((item) => {
                                const checked = checkedMarkets.has(item.id);

                                return (
                                    <MarketCardSelectable
                                        checked={checked}
                                        toggleChecked={() =>
                                            toggleMarketChecked(item.id)
                                        }
                                        market={item}
                                        key={item.id}
                                    />
                                );
                            })}
                        </Flex>
                    )}
                </StyledScrollContainer>

                <StyledControlsContainer>
                    <BaseButton
                        type="primary"
                        onClick={handleUpdatePoint}
                        disabled={!checkedMarkets.size}
                        loading={updateLoading}
                    >
                        Зберегти
                    </BaseButton>

                    <FloatingActionButton
                        onClick={handleCreateMarket}
                        icon={<PlusOutlined />}
                        right={16}
                        top={-50}
                        disabled={updateLoading}
                    />
                </StyledControlsContainer>
            </MainContainer>
        </>
    );
}

export default AddMarket;
